import React, { useState, useEffect, ChangeEvent } from "react";
import { useAppDispatch, useAppSelector } from "../../../store/hook";
import { RootState } from "../../../store/store";
import {
  creditPrincipalWallet,
  fetchPrincipalBankBalance,
  fetchPrincipalBanks,
  fetchPrincipalWalletBalance,
  fetchPrincipalWalletTransaction,
  fetchSelectedPrincipal,
  Principal,
} from "../../../features/principal/PrincipalSlice";
import PrincipalTopUpHeader from "./PrincipalTopUpHeader";
import PrincipalWalletCard from "./PrincipalWalletCard";
import PrincipalTopUpForm from "./PrincipalTopUpForm";
import LoadingErrorState from "./LoadingErrorState";
import PrincipalTransactionModal from "./PrincipalTransactionModal";
import StatementsModal from "./StatementsModal";
import { PrincipalWalletProgramOption } from "../../../features/principal/types";
import { useMoney } from "../../../hooks/useMoney";

interface TopUpPageProps {
  principal: Principal;
  onBack: () => void;
}

const ICICI_BANK_NAME = "ICICI Bank";

const PrincipalTopUpPage: React.FC<TopUpPageProps> = ({ principal, onBack }) => {
  const { locale } = useMoney();
  const formatAmount = (amount: number) =>
    amount.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const dispatch = useAppDispatch();
  const {
    selectedPrincipalLoading,
    selectedPrincipalError,
    principalWalletBalance,
    principalWalletBalanceLoading,
    principalWalletBalanceError,
    selectedPrincipalTransactions,
    selectedPrincipalTransactionsLoading,
    selectedPrincipalTransactionsError,
    principalBanks,
    principalBanksLoading,
    principalBanksError,
  } = useAppSelector((state: RootState) => state.principal);

  const [selectedProgramId, setSelectedProgramId] = useState<string>("");
  const [formData, setFormData] = useState({ remark: "", amount: "", reference: "", bankId: "" });
  const [formErrors, setFormErrors] = useState({ remark: "", amount: "", reference: "", bankId: "", programId: "" });
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [showStatementsModal, setShowStatementsModal] = useState(false);
  const [selectedWalletId, setSelectedWalletId] = useState<number | null>(null);
  const [selectedProgramNameForTransactions, setSelectedProgramNameForTransactions] = useState<string>("");
  const [bankBalance, setBankBalance] = useState<string>("0");
  const [bankBalanceLoading, setBankBalanceLoading] = useState(false);

  const wallets = principalWalletBalance?.wallets ?? [];

  const walletProgramOptions: PrincipalWalletProgramOption[] = wallets.map((wallet) => ({
    programId: wallet.programId,
    programName: wallet.program?.programName ?? wallet.name,
  }));

  // Resolve ICICI bank from the principal's associated banks
  const iciciBankEntry = principalBanks?.find(
    (bank) => bank.name?.toLowerCase().includes(ICICI_BANK_NAME.toLowerCase())
  );
  const iciciPrincipalBankId = iciciBankEntry?.bankId ?? null;

  const loadWalletBalance = () => {
    dispatch(fetchPrincipalWalletBalance(principal.principalId));
  };

  const refresh = (haveError: boolean) => {
    setFormErrors({ remark: "", amount: "", reference: "", bankId: "", programId: "" });
    setFormData({ remark: "", amount: "", reference: "", bankId: "" });
    setShowTopUpModal(false);
    if (!haveError) {
      loadWalletBalance();
    }
  };

  useEffect(() => {
    setShowTopUpModal(false);
    setSelectedProgramId("");
    dispatch(fetchSelectedPrincipal(principal.principalId));
    loadWalletBalance();
    dispatch(fetchPrincipalBanks(principal.principalId));
  }, [principal.principalId, dispatch]);

  useEffect(() => {
    if (wallets.length > 0 && !selectedProgramId) {
      setSelectedProgramId(wallets[0].programId.toString());
    }
  }, [principalWalletBalance, selectedProgramId]);

  // Fetch ICICI bank balance once it is resolved. If no bank is configured, or
  // the live balance call fails, the balance simply falls back to 0 rather than
  // hiding the bank balance / statements UI.
  useEffect(() => {
    if (!iciciPrincipalBankId) {
      setBankBalance("0");
      return;
    }
    const fetchBalance = async () => {
      setBankBalanceLoading(true);
      const response = await dispatch(
        fetchPrincipalBankBalance({
          principalId: principal.principalId,
          principalBankId: iciciPrincipalBankId,
        })
      );
      if (fetchPrincipalBankBalance.fulfilled.match(response) && response.payload.EFFECTIVEBAL) {
        setBankBalance(response.payload.EFFECTIVEBAL);
      } else {
        setBankBalance("0");
      }
      setBankBalanceLoading(false);
    };
    fetchBalance();
  }, [principal.principalId, iciciPrincipalBankId, dispatch]);

  const handleChange = (e: any) => {
    const { id, value } = e.target;
    setFormData({ ...formData, [id]: value });
    setFormErrors({ ...formErrors, [id]: "" });
  };

  const handleProgramChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSelectedProgramId(e.target.value);
    setFormErrors({ ...formErrors, programId: "" });
  };

  const addBalance = async (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    let hasError = false;

    if (!selectedProgramId) {
      hasError = true;
      setFormErrors((prev) => ({ ...prev, programId: "Please select a program" }));
    }
    if (!formData.amount) {
      hasError = true;
      setFormErrors((prev) => ({ ...prev, amount: "Please enter valid amount" }));
    } else if (parseInt(formData.amount) < 1) {
      hasError = true;
      setFormErrors((prev) => ({ ...prev, amount: "Amount should be at least more than 1" }));
    }
    if (!formData.bankId) {
      hasError = true;
      setFormErrors((prev) => ({ ...prev, bankId: "Please select a bank" }));
    }
    if (!formData.reference) {
      hasError = true;
      setFormErrors((prev) => ({ ...prev, reference: "Please enter a valid bank reference" }));
    } else if (!/^[a-zA-Z0-9]+$/.test(formData.reference)) {
      hasError = true;
      setFormErrors((prev) => ({ ...prev, reference: "Bank reference must contain only letters and numbers" }));
    }
    if (hasError) return;

    const response = await dispatch(
      creditPrincipalWallet({
        amount: formData.amount,
        bankId: formData.bankId,
        principalId: principal.principalId,
        programId: selectedProgramId,
        transactionNumber: formData.reference,
        remark: formData.remark,
      })
    );
    refresh(!response.type.includes("fulfilled"));
  };

  const handleViewTransactions = (walletId: number, programName: string) => {
    setSelectedWalletId(walletId);
    setSelectedProgramNameForTransactions(programName);
    setShowTransactionModal(true);
    dispatch(
      fetchPrincipalWalletTransaction({
        principalId: principal.principalId,
        walletId,
        limit: 50,
        sortBy: "createdAt",
        sortOrder: "DESC",
      })
    );
  };

  const handleCloseTransactionModal = () => {
    setShowTransactionModal(false);
    setSelectedWalletId(null);
    setSelectedProgramNameForTransactions("");
  };

  const walletBalanceFormatted = formatAmount(principalWalletBalance?.totalBalance ?? 0);
  const saralAmountFormatted = formatAmount(principalWalletBalance?.totalsaralAmount ?? 0);
  const bankBalanceFormatted = formatAmount(parseFloat(bankBalance) || 0);

  return (
    <div className="bg-white h-full">
      <PrincipalTopUpHeader
        principalName={principal.name}
        onBack={onBack}
        bankBalance={bankBalanceFormatted}
        bankBalanceLoading={bankBalanceLoading}
        walletBalance={walletBalanceFormatted}
        walletBalanceLoading={principalWalletBalanceLoading}
        saralAmount={saralAmountFormatted}
        onOpenStatements={() => setShowStatementsModal(true)}
        onAddTopUp={() => setShowTopUpModal(true)}
      />

      <div className="max-w-7xl mx-auto px-8 sm:px-6 lg:px-8">
        <LoadingErrorState loading={selectedPrincipalLoading} error={selectedPrincipalError} />

        {!selectedPrincipalLoading && !selectedPrincipalError && (
          <div className="bg-white rounded-xl p-8 border-[1px] border-cardborder mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Available Wallets</h2>
            {principalWalletBalanceError && (
              <p className="text-red-500 text-sm mb-4">{principalWalletBalanceError}</p>
            )}
            {principalWalletBalanceLoading && (
              <p className="text-gray-500 text-sm mb-4">Loading wallets...</p>
            )}
            <div className="space-y-8">
              {!principalWalletBalanceLoading &&
                wallets.map((wallet) => (
                  <PrincipalWalletCard
                    key={wallet.principalWalletId}
                    wallet={wallet}
                    onViewTransactions={handleViewTransactions}
                  />
                ))}
              {!principalWalletBalanceLoading && !principalWalletBalanceError && wallets.length === 0 && (
                <p className="text-gray-500 text-sm">No wallets found for this principal.</p>
              )}
            </div>
          </div>
        )}

        <PrincipalTopUpForm
          show={showTopUpModal}
          onHide={() => setShowTopUpModal(false)}
          formData={formData}
          formErrors={formErrors}
          selectedProgramId={selectedProgramId}
          programs={walletProgramOptions}
          programsLoading={principalWalletBalanceLoading}
          programsError={principalWalletBalanceError}
          isLoading={selectedPrincipalLoading}
          banks={principalBanks}
          banksLoading={principalBanksLoading}
          banksError={principalBanksError}
          onFormChange={handleChange}
          onProgramChange={handleProgramChange}
          onSubmit={addBalance}
        />

        <PrincipalTransactionModal
          show={showTransactionModal}
          onHide={handleCloseTransactionModal}
          transactions={selectedPrincipalTransactions?.transactions || null}
          loading={selectedPrincipalTransactionsLoading}
          error={selectedPrincipalTransactionsError}
          programName={selectedProgramNameForTransactions}
        />

        <StatementsModal
          show={showStatementsModal}
          onHide={() => setShowStatementsModal(false)}
          principalId={principal.principalId}
          principalName={principal.name}
          iciciPrincipalBankId={iciciPrincipalBankId}
          principalBanks={principalBanks}
        />
      </div>
    </div>
  );
};

export default PrincipalTopUpPage;
